import { Link, useParams } from 'react-router-dom'
import { Faq } from '../components/Faq'
import { qualifyingTrades, tradeBySlug, tradeFaq } from '../data/trades'
import { usePageMeta } from '../lib/usePageMeta'

const TH = 'text-left p-3 text-xs uppercase tracking-wide text-[var(--text-muted)]'

function joinPhrase(items: string[]): string {
  if (!items.length) return 'multiple lanes'
  if (items.length <= 2) return items.join(' and ')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

export function TradeHubPage() {
  const { slug: rawSlug } = useParams<{ slug: string }>()
  // Tolerate a trailing ".html"/slash (static hosting / direct file access) so a real trade hub never
  // falls through to the thin "not found" state.
  const slug = rawSlug?.replace(/\.html$/i, '').replace(/\/+$/, '')
  const t = slug ? tradeBySlug(slug) : undefined

  // Meta is set unconditionally (hooks can't be conditional). Unknown/under-bar slugs fall back to a
  // not-found meta; the static build only emits pages for qualifying trades and the edge middleware
  // only matches the shape, so a human reaches the not-found branch by client-navigating a bad slug.
  usePageMeta(
    t
      ? `${t.title} Sailing Schedules & Transit Times | SchedulesMCP`
      : 'Trade not found · SchedulesMCP',
    t
      ? `${t.title} ocean shipping schedules: ${t.lanes.length} lanes with forward sailings, carrier line-ups and transit times. Compare carriers on any ${t.title} lane before you book.`
      : 'That trade page does not exist. Browse the ocean lanes SchedulesMCP covers.'
  )

  if (!t) {
    return (
      <main className="max-w-3xl mx-auto px-6 pt-12 pb-16">
        <h1 className="font-display font-extrabold text-3xl mb-3">Trade not found</h1>
        <p className="text-[var(--text-secondary)] mb-6">
          We don't have a hub page for that trade.
        </p>
        <Link to="/lanes" className="text-signal no-underline hover:underline">
          See all lanes →
        </Link>
      </main>
    )
  }

  const busiest = t.lanes.slice(0, 3).map((l) => `${l.o_name} → ${l.d_name}`)
  const totalSailings = t.lanes.reduce((n, l) => n + (l.sailings || 0), 0)
  const others = qualifyingTrades().filter((x) => x.slug !== t.slug)

  return (
    <main className="max-w-4xl mx-auto px-6 pt-12 pb-16">
      <nav className="text-sm text-[var(--text-muted)] mb-5">
        <Link to="/" className="no-underline hover:underline">
          SchedulesMCP
        </Link>{' '}
        ›{' '}
        <Link to="/lanes" className="no-underline hover:underline">
          Lanes
        </Link>{' '}
        › {t.title}
      </nav>

      <span className="text-signal text-sm font-semibold tracking-wide">
        {t.title} trade · {t.lanes.length} lanes
      </span>
      <h1 className="font-display font-extrabold text-4xl mt-3 mb-4">
        {t.title} ocean shipping schedules
      </h1>
      <p className="text-[var(--text-secondary)] mb-6 max-w-2xl leading-relaxed">
        SchedulesMCP tracks {t.lanes.length} {t.title} ocean lanes with forward sailings (
        {totalSailings.toLocaleString('en-US')} sailings in all) led by {joinPhrase(busiest)}. Each
        lane is normalised into one shape, so you can line the carriers up on transit time before
        you book.
        {t.trade === 'Oceania' && (
          <>
            {' '}
            This is the South Pacific and island trade: coverage runs to Papua New Guinea, Fiji and
            New Caledonia alongside the wider Oceania network, where sailings are thinner and
            schedule discipline matters more than on the mainline trades.
          </>
        )}
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link
          to="/compare"
          className="bg-signal text-white font-bold px-6 py-3 rounded-lg no-underline hover:opacity-90"
        >
          Compare a lane live
        </Link>
        <Link
          to="/lanes"
          className="border border-border px-6 py-3 rounded-lg no-underline hover:border-signal"
        >
          All lanes
        </Link>
      </div>

      <h2 className="font-display font-bold text-xl mt-11 mb-3">{t.title} lanes</h2>
      <p className="text-[var(--text-secondary)] leading-relaxed max-w-2xl mb-4">
        Every {t.title} lane we carry, ranked by how many forward sailings we track, with the
        carrier line-up and median port-to-port transit. Open a lane for the full carrier table and
        upcoming departures.
      </p>
      <div className="overflow-x-auto border border-border rounded-xl">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className={TH}>Lane</th>
              <th className={TH}>Sailings</th>
              <th className={TH}>Carriers</th>
              <th className={TH}>Median transit</th>
            </tr>
          </thead>
          <tbody>
            {t.lanes.map((l) => (
              <tr key={l.slug} className="border-b border-border">
                <td className="p-3">
                  <Link
                    to={`/lanes/${l.slug}`}
                    className="text-signal font-semibold no-underline hover:underline"
                  >
                    {l.o_name} → {l.d_name}
                  </Link>
                </td>
                <td className="p-3 text-[var(--text-muted)] font-mono">{l.sailings}</td>
                <td className="p-3 text-[var(--text-muted)] font-mono">{l.carriers}</td>
                <td className="p-3 text-[var(--text-muted)] font-mono">
                  {l.median_transit != null ? `${l.median_transit} days` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Faq
        id={`trade-${t.slug}`}
        items={tradeFaq(t.title, t.lanes.length)}
        heading={`${t.title} ocean schedules: FAQ`}
      />

      <h2 className="font-display font-bold text-xl mt-12 mb-4">Other trades</h2>
      <div className="flex flex-wrap gap-2.5">
        {others.map((o) => (
          <Link
            key={o.slug}
            to={`/lanes/trade/${o.slug}`}
            className="bg-surface-0 border border-border rounded-full px-3.5 py-1.5 text-sm text-[var(--text-secondary)] no-underline hover:border-signal"
          >
            {o.title}
          </Link>
        ))}
        <Link
          to="/lanes"
          className="rounded-full px-3.5 py-1.5 text-sm text-signal no-underline hover:underline border"
          style={{ borderColor: 'rgb(var(--accent-rgb) / 0.3)' }}
        >
          All lanes →
        </Link>
      </div>
    </main>
  )
}

export default TradeHubPage
